import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Popover,
  Button,
  Checkbox,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Candidature,
  listCandidatures,
  updateCandidatureStatus,
} from '../api/candidatures';
import { listUsers } from '../api/users';
import { listPostsSummary } from '../api/posts';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const statusColor = (status?: string) => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'pending':
    default:
      return 'warning';
  }
};

export const CandidaturesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<Candidature[]>({
    queryKey: ['candidatures'],
    queryFn: listCandidatures,
  });
  const { data: users } = useQuery({
    queryKey: ['users-for-candidatures'],
    queryFn: listUsers,
  });
  const { data: posts } = useQuery({
    queryKey: ['posts-summary'],
    queryFn: listPostsSummary,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [orderBy, setOrderBy] = React.useState<
    'user' | 'post' | 'date' | 'createdAt' | 'updatedAt' | 'status'
  >('date');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');

  const [confirmAnchorEl, setConfirmAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [confirmDescription, setConfirmDescription] = React.useState('');
  const [pendingAction, setPendingAction] = React.useState<{
    ids: string[];
    status: 'approved' | 'rejected';
  } | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const confirmOpen = Boolean(confirmAnchorEl);

  const handleRequestSort = (
    property: 'user' | 'post' | 'date' | 'createdAt' | 'updatedAt' | 'status'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((c) => {
      const haystack = `${c.id} ${c.userId ?? ''} ${c.postId ?? ''}`.toLowerCase();
      const matchesSearch = haystack.includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || (c.status ?? 'pending') === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter]);

  const userNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    users?.forEach((u) => {
      const fullName = `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim();
      map[u.id] = fullName || u.email || u.id;
    });
    return map;
  }, [users]);

  const postTitleById = React.useMemo(() => {
    const map: Record<string, string> = {};
    posts?.forEach((p) => {
      map[p.id] = p.title || p.id;
    });
    return map;
  }, [posts]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      let av: string | Date | undefined;
      let bv: string | Date | undefined;

      switch (orderBy) {
        case 'user':
          av = userNameById[a.userId ?? ''] ?? (a.userId ?? '').toLowerCase();
          bv = userNameById[b.userId ?? ''] ?? (b.userId ?? '').toLowerCase();
          break;
        case 'post':
          av = postTitleById[a.postId ?? ''] ?? (a.postId ?? '').toLowerCase();
          bv = postTitleById[b.postId ?? ''] ?? (b.postId ?? '').toLowerCase();
          break;
        case 'date':
          av = a.date ?? new Date(0);
          bv = b.date ?? new Date(0);
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        case 'status':
          av = (a.status ?? '').toString().toLowerCase();
          bv = (b.status ?? '').toString().toLowerCase();
          break;
        default:
          av = 0 as any;
          bv = 0 as any;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy, userNameById, postTitleById]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleChangeStatusRequest = (
    ids: string[],
    status: 'approved' | 'rejected'
  ) => {
    if (!data || ids.length === 0) return;
    const actionLabel = status === 'approved' ? 'approuver' : 'rejeter';

    if (ids.length === 1) {
      const id = ids[0];
      const candidature = data.find((c) => c.id === id);
      const candidateName = candidature?.userId
        ? userNameById[candidature.userId] ?? candidature.userId
        : 'ce candidat';
      const postTitle = candidature?.postId
        ? postTitleById[candidature.postId] ?? candidature.postId
        : 'ce poste';

      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} la candidature de ${candidateName} pour ${postTitle} ?`
      );
    } else {
      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} les ${ids.length} candidatures sélectionnées ?`
      );
    }

    setPendingAction({ ids, status });
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    await Promise.all(
      pendingAction.ids.map((id) =>
        updateCandidatureStatus(id, pendingAction.status)
      )
    );
    await queryClient.invalidateQueries({ queryKey: ['candidatures'] });
    setConfirmAnchorEl(null);
    setPendingAction(null);
    setSelectedIds([]);
  };

  const handleCancelAction = () => {
    setConfirmAnchorEl(null);
    setPendingAction(null);
  };

  const allSelected =
    filtered.length > 0 &&
    filtered.every((c) => selectedIds.includes(c.id));
  const someSelected =
    filtered.length > 0 &&
    filtered.some((c) => selectedIds.includes(c.id)) &&
    !allSelected;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filtered.map((c) => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Candidatures
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="ID, candidat, poste..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="approved">Approuvée</MenuItem>
            <MenuItem value="rejected">Rejetée</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectedIds.length
            ? `${selectedIds.length} sélectionnée(s)`
            : 'Aucune sélection'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedIds.length}
            onClick={(event) => {
              setConfirmAnchorEl(event.currentTarget);
              handleChangeStatusRequest(selectedIds, 'approved');
            }}
          >
            Approuver la sélection
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={!selectedIds.length}
            onClick={(event) => {
              setConfirmAnchorEl(event.currentTarget);
              handleChangeStatusRequest(selectedIds, 'rejected');
            }}
          >
            Rejeter la sélection
          </Button>
        </Box>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell sortDirection={orderBy === 'user' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'user'}
                  direction={orderBy === 'user' ? order : 'asc'}
                  onClick={() => handleRequestSort('user')}
                >
                  Candidat
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'post' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'post'}
                  direction={orderBy === 'post' ? order : 'asc'}
                  onClick={() => handleRequestSort('post')}
                >
                  Poste
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'date' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'date'}
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={() => handleRequestSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créé le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mis à jour le
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((c) => (
                <TableRow key={c.id} hover selected={selectedIds.includes(c.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(c.id)}
                      onChange={() => toggleOne(c.id)}
                    />
                  </TableCell>
                  <TableCell>
                    {c.userId ? userNameById[c.userId] ?? c.userId : '—'}
                  </TableCell>
                  <TableCell>
                    {c.postId ? postTitleById[c.postId] ?? c.postId : '—'}
                  </TableCell>
                  <TableCell>{formatDate(c.date)}</TableCell>
                  <TableCell>{formatDate(c.createdAt)}</TableCell>
                  <TableCell>{formatDate(c.updatedAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={c.status ?? 'pending'}
                      color={statusColor(c.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {c.status !== 'approved' && (
                      <Tooltip title="Approuver la candidature">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            setConfirmAnchorEl(event.currentTarget);
                            handleChangeStatusRequest([c.id], 'approved');
                          }}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {c.status !== 'rejected' && (
                      <Tooltip title="Rejeter la candidature">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            setConfirmAnchorEl(event.currentTarget);
                            handleChangeStatusRequest([c.id], 'rejected');
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune candidature trouvée.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
      <Popover
        open={confirmOpen}
        anchorEl={confirmAnchorEl}
        onClose={handleCancelAction}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" gutterBottom>
            Confirmer l&apos;action
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {confirmDescription}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button onClick={handleCancelAction} color="inherit" size="small">
              Annuler
            </Button>
            <Button
              onClick={handleConfirmAction}
              color="primary"
              variant="contained"
              size="small"
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Popover>
    </Paper>
  );
};

